// covered-by: frontends/carrie/src/routes/settings/routes/__tests__/EnterpriseSsoSettingsPanel.test.tsx
import {
  Button,
  Card,
  Flex,
  Heading,
  Select,
  Text,
  TextField,
} from "@radix-ui/themes";
import { useEffect, useState } from "react";

import { useUI } from "../../../contexts/ui";
import {
  getEnterpriseAuth,
  updateEnterpriseAuth,
  type EnterpriseAuthPayload,
} from "../../../services/enterprises";

type SsoMode = "off" | "optional" | "enforced";

export function EnterpriseSsoSettingsPanel({
  enterpriseId,
}: {
  enterpriseId: string;
}) {
  const { notify } = useUI();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [ssoMode, setSsoMode] = useState<SsoMode>("off");
  const [workosOrganizationId, setWorkosOrganizationId] = useState("");
  const [ssoDomains, setSsoDomains] = useState("");
  const [directorySyncEnabled, setDirectorySyncEnabled] = useState(false);
  const [sessionMaxAgeDays, setSessionMaxAgeDays] = useState("");
  const [adminPortalUrl, setAdminPortalUrl] = useState<string | undefined>();
  const [confirmEnforce, setConfirmEnforce] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getEnterpriseAuth(enterpriseId)
      .then((res) => {
        if (cancelled) return;
        const auth = res.auth;
        setSsoMode((auth.ssoMode as SsoMode) || "off");
        setWorkosOrganizationId(auth.workosOrganizationId || "");
        setSsoDomains((auth.ssoDomains || []).join(", "));
        setDirectorySyncEnabled(Boolean(auth.directorySyncEnabled));
        setSessionMaxAgeDays(
          auth.sessionMaxAgeDays != null ? String(auth.sessionMaxAgeDays) : "",
        );
        setAdminPortalUrl(res.adminPortalUrl);
      })
      .catch(() => {
        if (cancelled) return;
        notify({
          variant: "error",
          title: "SSO settings",
          message: "Could not load enterprise auth settings.",
        });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enterpriseId, notify]);

  async function handleSave() {
    if (ssoMode === "enforced" && !confirmEnforce) {
      notify({
        variant: "error",
        title: "Confirm enforcement",
        message:
          "Check the confirmation box before enforcing SSO. Password login will be blocked for members.",
      });
      return;
    }
    setSaving(true);
    try {
      const body: EnterpriseAuthPayload = {
        ssoMode,
        workosOrganizationId: workosOrganizationId.trim() || undefined,
        ssoDomains: ssoDomains
          .split(",")
          .map((d) => d.trim().toLowerCase())
          .filter(Boolean),
        directorySyncEnabled,
      };
      if (sessionMaxAgeDays.trim()) {
        body.sessionMaxAgeDays = Number(sessionMaxAgeDays);
      }
      await updateEnterpriseAuth(enterpriseId, body);
      notify({
        variant: "success",
        title: "SSO settings saved",
        message:
          ssoMode === "off"
            ? "SSO disabled — password login restored for this enterprise."
            : ssoMode === "optional"
              ? "Optional SSO enabled — password still works while users link SSO."
              : "SSO enforced — members must use the identity provider.",
      });
      setConfirmEnforce(false);
    } catch (err) {
      notify({
        variant: "error",
        title: "SSO settings",
        message:
          err instanceof Error ? err.message : "Failed to update SSO settings.",
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card data-testid="settings-enterprise-sso-card">
      <Flex direction="column" gap="3" p="4">
        <Heading as="h2" size="4" weight="medium">
          Single sign-on (SSO)
        </Heading>
        <Text size="2" color="gray">
          Per-enterprise WorkOS SSO. Default is off (unchanged password login).
          Use optional while users link accounts, then enforce when ready.
          Rolling back from enforced to optional restores password login.
        </Text>

        {loading ? (
          <Text size="2" data-testid="settings-enterprise-sso-loading">
            Loading…
          </Text>
        ) : (
          <>
            <label htmlFor="enterprise-sso-mode">
              <Text as="div" size="2" mb="1" weight="bold">
                SSO mode
              </Text>
              <Select.Root
                value={ssoMode}
                onValueChange={(v) => {
                  const next = v as SsoMode;
                  setSsoMode(next);
                  // Clearing the acknowledgement when leaving enforced ensures
                  // re-selecting enforced always requires a fresh confirmation
                  // instead of silently reusing a stale checked box.
                  if (next !== "enforced") setConfirmEnforce(false);
                }}
              >
                <Select.Trigger
                  id="enterprise-sso-mode"
                  data-testid="settings-enterprise-sso-mode"
                  aria-label="SSO mode"
                />
                <Select.Content>
                  <Select.Item value="off">Off (password only)</Select.Item>
                  <Select.Item value="optional">
                    Optional (password + SSO)
                  </Select.Item>
                  <Select.Item value="enforced">
                    Enforced (SSO only)
                  </Select.Item>
                </Select.Content>
              </Select.Root>
            </label>

            <label htmlFor="enterprise-sso-org">
              <Text as="div" size="2" mb="1" weight="bold">
                WorkOS organization ID
              </Text>
              <TextField.Root
                id="enterprise-sso-org"
                data-testid="settings-enterprise-sso-org"
                aria-label="WorkOS organization ID"
                value={workosOrganizationId}
                onChange={(e) => setWorkosOrganizationId(e.target.value)}
                placeholder="org_…"
              />
            </label>

            <label htmlFor="enterprise-sso-domains">
              <Text as="div" size="2" mb="1" weight="bold">
                SSO domains (comma-separated)
              </Text>
              <TextField.Root
                id="enterprise-sso-domains"
                data-testid="settings-enterprise-sso-domains"
                aria-label="SSO domains"
                value={ssoDomains}
                onChange={(e) => setSsoDomains(e.target.value)}
                placeholder="acme.com, acme.co.uk"
              />
            </label>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                data-testid="settings-enterprise-sso-dsync"
                aria-label="Enable directory sync"
                checked={directorySyncEnabled}
                onChange={(e) => setDirectorySyncEnabled(e.target.checked)}
              />
              <Text size="2">Directory Sync (SCIM) enabled</Text>
            </label>

            <label htmlFor="enterprise-sso-session-days">
              <Text as="div" size="2" mb="1" weight="bold">
                Session max age (days, optional)
              </Text>
              <TextField.Root
                id="enterprise-sso-session-days"
                data-testid="settings-enterprise-sso-session-days"
                aria-label="Session max age in days"
                value={sessionMaxAgeDays}
                onChange={(e) => setSessionMaxAgeDays(e.target.value)}
                placeholder="e.g. 14"
                type="number"
                min={1}
              />
            </label>

            {ssoMode === "enforced" ? (
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  data-testid="settings-enterprise-sso-confirm-enforce"
                  aria-label="Confirm SSO enforcement"
                  checked={confirmEnforce}
                  onChange={(e) => setConfirmEnforce(e.target.checked)}
                />
                <Text size="2">
                  I understand password login will be blocked for members until
                  rollback to optional/off.
                </Text>
              </label>
            ) : null}

            <Flex gap="3" wrap="wrap">
              <Button
                data-testid="settings-enterprise-sso-save"
                aria-label="Save SSO settings"
                loading={saving}
                onClick={() => void handleSave()}
              >
                Save SSO settings
              </Button>
              {adminPortalUrl ? (
                <a
                  href={adminPortalUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="settings-enterprise-sso-portal"
                  aria-label="Configure SSO in WorkOS Admin Portal"
                  className="inline-flex items-center rounded-md border px-3 py-2 text-sm font-medium"
                >
                  Configure SSO (Admin Portal)
                </a>
              ) : null}
            </Flex>
          </>
        )}
      </Flex>
    </Card>
  );
}
